import { isEmpty } from "lodash";
import cx from "classnames";
import * as React from "react";
import { useSelector } from "react-redux";
// eslint-disable-next-line no-restricted-imports
import { Banner } from "../scheduling/components/Banner/Banner";
// eslint-disable-next-line no-restricted-imports
import { PortalBanner } from "../scheduling/components/Banner/PortalBanner";
import { useErrorToast } from "features/patientDashboard/hooks/useErrorToast";
import stylesV2 from "./Layout.module.scss";
import stylesV3 from "./LayoutV2.module.scss";
import { Navbar } from "components/Navbar";
import { FooterPortal } from "components/Footer/Footer";
import { selectSessionOffice } from "features/patientDashboard/patientDashboardSlice";
import { getDisplayBannerRequest } from "api/getDisplayBannerRequest";
import { MainBanner } from "../scheduling/components/Banner/MainBanner";
import { DisplayBanner } from "../scheduling/components/Banner/DisplayBanner";
import { useLocation } from "react-router-dom";
import { useSchedulingABVariant } from "../scheduling/utils/useSchedulingABVariant";

type LayoutProps = {
  children?: React.ReactNode;
  navbar?: React.ReactNode;
  showBanner?: boolean;
  showSchedulerBanner?: boolean;
  emptyHeight?: boolean;
  calledFrom?: string;
  office?: any;
  fullContainer?: boolean;
  mainBanner?: any;
  showLogout?: boolean;
  scheduleBanner?: boolean;
  bannerTypes?: string[];
};

export const Layout: React.FC<LayoutProps> = ({
  children,
  navbar,
  showBanner = false,
  showSchedulerBanner = false,
  emptyHeight = false,
  calledFrom,
  mainBanner,
  fullContainer,
  showLogout,
  scheduleBanner,
  bannerTypes,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const sessionOffice = useSelector(selectSessionOffice);
  const { showErrorToast } = useErrorToast();
  const [bannerData, setBannerData] = React.useState<any[] | undefined>();
  React.useEffect(() => {
      async function getBannerDate() {
        try {
          const data = await getDisplayBannerRequest();
          setBannerData(data);
        } catch (e) {
          showErrorToast();
        }
      }
      if (!bannerData && scheduleBanner) getBannerDate();
    }, [bannerData, scheduleBanner, showErrorToast]);

  const allowedTypes = bannerTypes;
  const orderedBanners =
    bannerData && scheduleBanner && Array.isArray(bannerData.order)
      ? bannerData.order
          .filter((type: string) => !allowedTypes || allowedTypes.includes(type))
          .map((type: string) => bannerData.banners?.[type])
          .filter(Boolean)
      : [];
  const hasAnyBanner =
    orderedBanners.length > 0 || !!mainBanner || showBanner || showSchedulerBanner;

  return (
    <div
      className={(fullContainer) ? styles.LayoutFull : styles.layout}
    >
      {hasAnyBanner && (
        <div className={styles.bannerStack}>
          {orderedBanners.map((banner: any) => (
            <DisplayBanner key={banner.id || banner.banner_type} bannerSetting={banner} />
          ))}
          {mainBanner && <MainBanner mainSetting={mainBanner} />}
          {showBanner && <PortalBanner />}
          {showSchedulerBanner && <Banner />}
        </div>
      )}
      <div className={cx(styles.navbarWrapper, hasAnyBanner && styles.navbarWithBanner)}>
        {navbar ? (
          navbar
        ) : !isEmpty(sessionOffice) ? (
          <Navbar office={sessionOffice} />
        ) : (
          <Navbar showLogout={showLogout}/>
        )}
      </div>

      <div className={styles.content}>{children}</div>
      <FooterPortal />
      {emptyHeight && (
        <div
          className={
            calledFrom && calledFrom === "perscription"
              ? styles.emptyHeight2
              : calledFrom && calledFrom === "summaryPage"
              ? styles.summaryHeight
              : calledFrom && calledFrom === "sadLayout"
              ? styles.sadLayoutHeight
              : styles.emptyHeight
          }
        ></div>
      )}
    </div>
  );
};
export const LayoutV2: React.FC<LayoutProps> = ({
  children,
  navbar,
  office,
  scheduleBanner,
  bannerTypes
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const { showErrorToast } = useErrorToast();
  const [bannerData, setBannerData] = React.useState<any[] | undefined>();
  React.useEffect(() => {
      async function getBannerDate() {
        try {
          const data = await getDisplayBannerRequest();
          setBannerData(data);
        } catch (e) {
          showErrorToast();
        }
      }
      if (!bannerData && scheduleBanner) getBannerDate();
    }, [bannerData, office, scheduleBanner, showErrorToast]);

  const allowedTypes =
    bannerTypes ||
    (office
      ? [
          "global",
          "promo",
          ...(office.supportsMedicaid !== true ? ["medicaid"] : []),
          ...(office.showOfficeBanner ? ["office"] : [])
        ]
      : undefined);
  const orderedBanners =
    bannerData && scheduleBanner && Array.isArray(bannerData.order)
      ? bannerData.order
          .filter((type: string) => !allowedTypes || allowedTypes.includes(type))
          .map((type: string) => bannerData.banners?.[type])
          .filter(Boolean)
      : [];
  const hasAnyBanner =
    orderedBanners.length > 0;

  return (
    <div className={styles.layoutV2}>
      {hasAnyBanner && (
        <div className={styles.bannerStack}>
          {office.showBrazeBanner && <div id="global-banner-container"></div>}
          {orderedBanners.map((banner: any) => (
            <DisplayBanner key={banner.id || banner.banner_type} bannerSetting={banner} />
          ))}
          <Banner />
        </div>
      )}
      <div className={cx(styles.navbarWrapper, hasAnyBanner && styles.navbarWithBanner)}>
        {navbar ? navbar : office ? <Navbar office={office} /> : <Navbar />}
      </div>

      <div className={styles.contentV2}>{children}</div>
    </div>
  );
};
